import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { useMerchantScope } from '@/hooks/use-merchant-scope';
import { useServiceMap } from '@/hooks/use-service-map';
import {
  IQ_TX_ID_MAX_LENGTH,
  type CascadeStage,
  type SearchAxis,
} from '@/hooks/use-transaction-search-cascade';
import { Button, Input, Label, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui';
import type { ComboboxOption } from '@/components/ui/combobox';
import { MerchantSelect } from '@/components/features/merchants/merchant-select';
import { MultiSelectFilter } from '@/components/shared/multi-select-filter';
import { FILTER_ALL_SENTINEL } from '@/components/shared/filter-constants';
import { DateRangePicker, type DateRange } from '@/components/shared/date-range-picker';
import {
  decodeFilterAxis,
  encodeFilterAxis,
  type FilterAxisValue,
} from '@/lib/utils/filter-axis';
import type { ListAdminTransactionsParams, TransactionStatus, GatewayMode } from '@/types/admin-transaction';

const ALL_STATUSES: TransactionStatus[] = [
  'pending', 'awaiting_identification', 'identified',
  'awaiting_consent', 'setup_complete', 'charged',
  'renewed', 'failed', 'cancelled', 'expired',
];

const FLOW_TYPES = ['WEB_TAN', 'WAP_CLICK'] as const;
const GATEWAY_MODES: GatewayMode[] = ['live', 'mock'];
// Spec 409 / Phase 6: Operator-Werte (Facade-Listen-Payload liefert OperatorID optional je Zeile).
const OPERATOR_IDS = ['D1_DE', 'D2_DE', 'O2_DE'] as const;
// Spec 296: Seitengroessen. Obergrenze 100 — die BFF cappt serverseitig auf
// max(100) (admin-transaction.ts), die UI bietet nie mehr an.
const PAGE_SIZES = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;

/**
 * Spec 479: aktiven Suchbegriff (Achse + Term) aus den URL-Filtern ableiten —
 * Deep-Link-Restore + Achsen-Chip. Praezedenz wie seit #475: bei mehreren
 * gesetzten Params (nur per Hand-URL erreichbar) gewinnt fuer die Anzeige die
 * teuerste Achse; das BFF bleibt permissiv und UND-verknuepft alle.
 * #482: `customerReferenceExact` ist die neue Kaskaden-Referenz-Achse (GSI,
 * billig) — der Teilstring-Param bleibt als Deep-Link-Legacy lesbar und
 * gewinnt als teuerste Achse weiterhin die Anzeige.
 */
export function activeSearchFromFilters(
  filters: ListAdminTransactionsParams,
): { axis: SearchAxis; term: string } {
  if (filters.customerReference) {
    return { axis: 'customerReference', term: filters.customerReference };
  }
  if (filters.txId) return { axis: 'txId', term: filters.txId };
  if (filters.customerReferenceExact) {
    return { axis: 'customerReferenceExact', term: filters.customerReferenceExact };
  }
  return { axis: 'internetqTransactionId', term: filters.internetqTransactionId ?? '' };
}

/**
 * Spec 479: Ein-Feld-ID-Suche mit explizitem Submit — die Typwahl entfaellt,
 * bei Submit laeuft seitenseitig die iterative Kaskade (IQ-ID → Facade-TX-ID →
 * Abo-ID → Zahlungsreferenz exakt → txId-Scan, Stopp beim ersten Treffer,
 * #482-Reihenfolge: alle indizierten Stufen vor dem Scan). Vollstaendig
 * kontrolliert: die Seite besitzt den Entwurf (Miss-Semantik „Begriff bleibt
 * stehen") und den Kaskaden-Lauf. Waehrend des Laufs ist der Submit gesperrt;
 * der Fortschritt ist eine ARIA-Live-Region — bei 30–60 s Laufzeit muss auch
 * ein Screenreader-Nutzer hoeren, dass (und wo) gesucht wird.
 */
function TransactionSearchControl({
  term,
  onTermChange,
  onSubmit,
  isSearching,
  runningStage,
}: {
  term: string;
  onTermChange: (value: string) => void;
  onSubmit: () => void;
  isSearching: boolean;
  runningStage: CascadeStage | null;
}) {
  const { t } = useTranslation('transactions');
  return (
    <div>
      <Label className="text-xs text-muted-foreground">
        {t('list.search.label', 'ID-Suche')}
      </Label>
      <div className="flex gap-1.5">
        {/* #482: laengstes verbleibendes Stufen-Gate ist die IQ-ID (128) —
            der 500er-Teilstring-Deckel entfiel mit der Referenz-Exakt-Stufe. */}
        <Input
          className="w-[280px]"
          value={term}
          maxLength={IQ_TX_ID_MAX_LENGTH}
          placeholder={t('list.search.placeholder')}
          disabled={isSearching}
          onChange={(e) => onTermChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isSearching) onSubmit();
          }}
        />
        <Button
          variant="secondary"
          className="h-9 px-3"
          onClick={onSubmit}
          disabled={isSearching}
          aria-label={t('list.search.submit', 'Suchen')}
        >
          <Search className="h-4 w-4" />
        </Button>
      </div>
      {/* Live-Region bleibt permanent gemountet — nur so kuendigen Screenreader
          den hineinwechselnden Fortschrittstext zuverlaessig an. */}
      <p role="status" aria-live="polite" className="mt-1 max-w-[400px] text-xs text-muted-foreground">
        {isSearching && runningStage
          ? t('list.search.searching', { stage: t(`list.search.stages.${runningStage}`) })
          : ''}
      </p>
      {/* Statischer Kaskaden-Hinweis: IMMER sichtbar (Plan Phase 3), der
          Fortschritt erscheint waehrend des Laufs zusaetzlich darueber. */}
      <p className="max-w-[400px] text-xs text-muted-foreground">
        {t('list.search.cascadeHint')}
      </p>
    </div>
  );
}

interface TransactionFiltersProps {
  /** Server-seitige Filter (resetten Cursor) */
  filters: ListAdminTransactionsParams;
  onFiltersChange: (filters: ListAdminTransactionsParams) => void;
  /** Client-seitige Filter (kein Cursor-Reset) */
  amountMin?: string;
  amountMax?: string;
  onAmountMinChange: (value: string) => void;
  onAmountMaxChange: (value: string) => void;
  dateFrom?: Date;
  dateTo?: Date;
  onDateRangeChange: (range: DateRange) => void;
  /** Issue #300: Reset-Button nur zeigen, wenn irgendein Filter aktiv ist. */
  canReset?: boolean;
  onReset?: () => void;
  /** Spec 479: Ein-Feld-Suche (kontrolliert — die Seite besitzt Entwurf + Kaskade). */
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  onSearchSubmit: () => void;
  /**
   * Spec 479: Kaskade laeuft — sperrt Submit UND alle Server-Filter-Controls
   * inkl. Reset (ein Filterwechsel mid-flight wuerde den geseedeten Cache-Key
   * gegen die Seiten-Query verschieben, D6). Client-Filter (Betrag/Datum)
   * bleiben aktiv, sie sind nicht Teil des Query-Keys.
   */
  isSearching?: boolean;
  runningStage?: CascadeStage | null;
}

export function TransactionFilters({
  filters,
  onFiltersChange,
  amountMin,
  amountMax,
  onAmountMinChange,
  onAmountMaxChange,
  dateFrom,
  dateTo,
  onDateRangeChange,
  canReset = false,
  onReset,
  searchTerm,
  onSearchTermChange,
  onSearchSubmit,
  isSearching = false,
  runningStage = null,
}: TransactionFiltersProps) {
  const { t } = useTranslation('transactions');
  // Spec 0220/§6: Merchant-Filter fuer alle Platform-Rollen (vereinheitlicht
  // ueber useMerchantScope statt nur platform_admin).
  const { showMerchant } = useMerchantScope();
  // Spec 409: Service-Katalog fuer Optionen (Suche nach serviceId UND DisplayName)
  // + Chip-Label-Aufloesung. /services ist tenant-scoped fuer Merchant-Rollen.
  const { services, resolveServiceLabel } = useServiceMap();

  // Spec 409: decode/encode der Mehrfach-Achsen aus den `+/-`-URL-Strings.
  const serviceAxis = useMemo(() => decodeFilterAxis(filters.service), [filters.service]);
  const statusAxis = useMemo(() => decodeFilterAxis(filters.status), [filters.status]);
  const operatorAxis = useMemo(() => decodeFilterAxis(filters.operator), [filters.operator]);

  const operatorOptions = useMemo<ComboboxOption[]>(
    () => OPERATOR_IDS.map((o) => ({ value: o, label: o })),
    [],
  );

  const serviceOptions = useMemo<ComboboxOption[]>(
    () =>
      services.map((s) => ({
        value: s.serviceId,
        label: resolveServiceLabel(s.serviceId),
        description: s.serviceId,
      })),
    [services, resolveServiceLabel],
  );

  const statusOptions = useMemo<ComboboxOption[]>(
    () => ALL_STATUSES.map((s) => ({ value: s, label: t(`statuses.${s}`) })),
    [t],
  );

  const setServiceAxis = (axis: FilterAxisValue) => {
    onFiltersChange({ ...filters, service: encodeFilterAxis(axis), cursor: undefined });
  };

  const setStatusAxis = (axis: FilterAxisValue) => {
    onFiltersChange({ ...filters, status: encodeFilterAxis(axis), cursor: undefined });
  };

  const setOperatorAxis = (axis: FilterAxisValue) => {
    onFiltersChange({ ...filters, operator: encodeFilterAxis(axis), cursor: undefined });
  };

  const handleMerchantChange = (value: string | undefined) => {
    onFiltersChange({ ...filters, merchantId: value, cursor: undefined });
  };

  const handleFlowTypeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      flowType: value === FILTER_ALL_SENTINEL ? undefined : (value as 'WEB_TAN' | 'WAP_CLICK'),
      cursor: undefined,
    });
  };

  const handleGatewayModeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      gatewayMode: value === FILTER_ALL_SENTINEL ? undefined : (value as GatewayMode),
      cursor: undefined,
    });
  };

  // Spec 296: Seitengroesse aendern — setzt limit und resettet den Cursor wie
  // jeder Server-Filter.
  const handlePageSizeChange = (value: string) => {
    onFiltersChange({ ...filters, limit: Number(value), cursor: undefined });
  };

  return (
    <div className="mb-4 space-y-3" role="search">
      {/* Zeile 1: Mehrfach-Filter (Status, Service) + Merchant + TX-ID-Suche */}
      <div className="flex flex-wrap items-start gap-3">
        <div className="w-[240px]">
          <Label className="text-xs text-muted-foreground">{t('list.filters.status')}</Label>
          <MultiSelectFilter
            value={statusAxis}
            onChange={setStatusAxis}
            options={statusOptions}
            disabled={isSearching}
            placeholder={t('list.filters.statusPlaceholder', 'Beliebiger Status')}
          />
        </div>

        {showMerchant && (
          <div>
            <Label className="text-xs text-muted-foreground">{t('list.filters.merchant')}</Label>
            <MerchantSelect
              value={filters.merchantId}
              onChange={handleMerchantChange}
              disabled={isSearching}
              className="w-[220px]"
            />
          </div>
        )}

        <div className="w-[260px]">
          <Label className="text-xs text-muted-foreground">{t('list.filters.service')}</Label>
          <MultiSelectFilter
            value={serviceAxis}
            onChange={setServiceAxis}
            options={serviceOptions}
            allowFreeText
            labelResolver={resolveServiceLabel}
            disabled={isSearching}
            placeholder={t('list.filters.servicePlaceholder', 'Beliebiger Service')}
            searchPlaceholder={t('list.filters.serviceSearch', 'Nach ID oder Name suchen…')}
          />
        </div>

        <div className="w-[220px]">
          <Label className="text-xs text-muted-foreground">{t('list.filters.operator', 'Operator')}</Label>
          <MultiSelectFilter
            value={operatorAxis}
            onChange={setOperatorAxis}
            options={operatorOptions}
            disabled={isSearching}
            placeholder={t('list.filters.operatorPlaceholder', 'Beliebiger Operator')}
          />
        </div>

        {/* Spec 479: Ein-Feld-Suche (Kaskade) — ersetzt das #475-Drei-Typen-Control */}
        <TransactionSearchControl
          term={searchTerm}
          onTermChange={onSearchTermChange}
          onSubmit={onSearchSubmit}
          isSearching={isSearching}
          runningStage={runningStage}
        />
      </div>

      {/* Zeile 2: einwertige Server-Filter (FlowType/Mode), Client (Betrag/Datum), Reset, PageSize */}
      <div className="flex flex-wrap items-end gap-3">
        <Select
          value={filters.flowType ?? FILTER_ALL_SENTINEL}
          onValueChange={handleFlowTypeChange}
          disabled={isSearching}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder={t('list.filters.flowType', 'Flow Type')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={FILTER_ALL_SENTINEL}>{t('list.filters.allFlowTypes', 'Alle Flows')}</SelectItem>
            {FLOW_TYPES.map((ft) => (
              <SelectItem key={ft} value={ft}>{ft}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.gatewayMode ?? FILTER_ALL_SENTINEL}
          onValueChange={handleGatewayModeChange}
          disabled={isSearching}
        >
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder={t('list.filters.mode', 'Modus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={FILTER_ALL_SENTINEL}>{t('list.filters.allModes', 'Alle Modi')}</SelectItem>
            {GATEWAY_MODES.map((gm) => (
              <SelectItem key={gm} value={gm}>
                {gm.toUpperCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-end gap-1.5">
          <div>
            <Label className="text-xs text-muted-foreground">{t('list.filters.amountMin', 'Min EUR')}</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={amountMin ?? ''}
              onChange={(e) => onAmountMinChange(e.target.value)}
              className="w-[100px]"
            />
          </div>
          <span className="pb-2 text-muted-foreground">–</span>
          <div>
            <Label className="text-xs text-muted-foreground">{t('list.filters.amountMax', 'Max EUR')}</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="999.99"
              value={amountMax ?? ''}
              onChange={(e) => onAmountMaxChange(e.target.value)}
              className="w-[100px]"
            />
          </div>
        </div>

        <DateRangePicker
          from={dateFrom}
          to={dateTo}
          onChange={onDateRangeChange}
        />

        {/* Issue #300: Reset — nur sichtbar bei aktiven Filtern, setzt die URL
            auf nacktes /transactions zurueck (alle Filter weg, limit auf Default). */}
        {canReset && onReset && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            disabled={isSearching}
            className="h-9 text-muted-foreground"
          >
            <X className="mr-1.5 h-4 w-4" />
            {t('list.filters.reset', 'Filter zurücksetzen')}
          </Button>
        )}

        {/* Spec 296: Page-Size-Selektor, rechts ausgerichtet */}
        <div className="ml-auto">
          <Label className="text-xs text-muted-foreground">
            {t('list.filters.pageSize', 'Pro Seite')}
          </Label>
          <Select
            value={String(filters.limit ?? DEFAULT_PAGE_SIZE)}
            onValueChange={handlePageSizeChange}
            disabled={isSearching}
          >
            <SelectTrigger className="w-[90px]" aria-label={t('list.filters.pageSize', 'Pro Seite')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
